import React, {useEffect, useState} from 'react';
import Translate from '../../../../../../../../components/Translate';
import LoadingComponent from '../../../../../../../../components-new/ui/LoadingComponent';
import TableDateTime from '../../../../../../../../components-new/table/TableDateTime';
import {getWebhookLogs} from '../../../../../../../../services/company/api';
import ChevronDownIcon from '../../../../../../../../assets/icons/ChevronDownIcon';
import WebhookTypeChip from './WebhookTypeChip';
import './WebhookLogs.css';

/*
 * What a webhook has been doing.
 *
 * A failing webhook used to leave one line in the server log, which the company
 * that owns the endpoint cannot read - so "it is not arriving" could be neither
 * shown nor disproved, and every such report became a developer's afternoon.
 *
 * A row at a time, newest first: when, what came back, and how long it took. The
 * bodies are behind the row rather than in it - they are the answer to "why",
 * which is asked of one call, not of fifty.
 *
 * It opens inside the webhooks modal rather than on top of it: a second modal
 * over the first leaves two dialogs stacked, two ways to close, and a question
 * about which of them the Escape key belongs to. This is the same box showing
 * something else, with a way back.
 */
const WebhookLogs = ({webhook}) => {
    const [logs, setLogs] = useState(null);
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        let active = true;

        getWebhookLogs(webhook.companyId, webhook.id)
            .then(rows => {
                if (active) setLogs(Array.isArray(rows) ? rows : []);
            })
            .catch(() => {
                if (active) setLogs([]);
            });

        return () => {
            active = false;
        };
    }, [webhook.companyId, webhook.id]);

    /*
     * A body, laid out.
     *
     * It is stored as it went over the wire - one line, no spaces - which is
     * right for sending and unreadable for looking at: the whole visitor object
     * arrives as one line that wraps six times. Parsed and printed again it is a
     * shape the eye can walk down.
     *
     * Anything that is not json is left exactly as it came: an endpoint that
     * answered with an HTML error page said something, and tidying it would lose
     * the one clue in it.
     */
    const pretty = (body) => {
        if (!body) return body;

        try {
            return JSON.stringify(JSON.parse(body), null, 2);
        } catch (e) {
            return body;
        }
    };

    /*
     * Three outcomes, not two. An endpoint that answered 500 is a different thing
     * from one that never answered at all - the first is their code, the second
     * is the address, the network or the timeout - and the operator chasing it
     * needs to know which.
     */
    const toneOf = (log) => {
        if (log.responseStatus === null || log.responseStatus === undefined) return 'failed';
        return log.responseStatus >= 200 && log.responseStatus < 300 ? 'ok' : 'error';
    };

    return (
        <React.Fragment>
            {logs === null && <LoadingComponent/>}

            {logs !== null && logs.length === 0 && (
                <div className="webhook-logs__empty"><Translate text="webhookLogsEmpty"/></div>
            )}

            {logs !== null && logs.length > 0 && (
                <div className="webhook-logs">
                    {logs.map(log => (
                        <div className={'webhook-logs__row webhook-logs__row--' + toneOf(log)} key={log.id}>
                            <button
                                type="button"
                                className="webhook-logs__head"
                                onClick={() => setOpenId(openId === log.id ? null : log.id)}
                            >
                                <span className={'webhook-logs__status webhook-logs__status--' + toneOf(log)}>
                                    {log.responseStatus === null || log.responseStatus === undefined
                                        ? <Translate text="webhookNoResponse"/>
                                        : log.responseStatus}
                                </span>

                                <span className="webhook-logs__when">
                                    <TableDateTime value={log.createdAt}/>
                                </span>

                                {log.durationMs !== null && log.durationMs !== undefined && (
                                    <span className="webhook-logs__duration">{log.durationMs} ms</span>
                                )}

                                <span className="webhook-logs__type">
                                    <WebhookTypeChip type={log.type}/>
                                </span>

                                {/* nothing said the row opened - it turns rather
                                    than appearing, so the shut state carries the
                                    same mark as the open one */}
                                <ChevronDownIcon
                                    className={'webhook-logs__chevron' + (openId === log.id ? ' is-open' : '')}
                                    width={16}
                                    height={16}
                                    stroke="currentColor"
                                    strokeWidth={2}
                                />
                            </button>

                            {openId === log.id && (
                                <div className="webhook-logs__body">
                                    {log.errorMessage && (
                                        <div className="webhook-logs__part">
                                            <span className="webhook-logs__label">
                                                <Translate text="webhookError"/>
                                            </span>
                                            <pre className="webhook-logs__pre webhook-logs__pre--error">
                                                {log.errorMessage}
                                            </pre>
                                        </div>
                                    )}

                                    <div className="webhook-logs__part">
                                        <span className="webhook-logs__label">
                                            <Translate text="webhookRequest"/>
                                        </span>
                                        <pre className="webhook-logs__pre">{pretty(log.requestBody)}</pre>
                                    </div>

                                    {log.responseBody && (
                                        <div className="webhook-logs__part">
                                            <span className="webhook-logs__label">
                                                <Translate text="webhookResponse"/>
                                            </span>
                                            <pre className="webhook-logs__pre">{pretty(log.responseBody)}</pre>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </React.Fragment>
    );
};

export default WebhookLogs;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/resources/webhooks/WebhookLogs.jsx